import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Section from 'components/layout/section';
import SectionHeader from 'components/layout/sectionHeader';
import SectionHeaderTitle from 'components/layout/sectionHeaderTitle';
import PageTitle from 'features/navigation/pageTitle';
import PasswordInput from 'components/inputs/passwordInput';
import Button from 'components/inputs/button';
import ButtonGroup from 'components/buttons/buttonGroup';
import ProcessingButton from 'components/inputs/processingButton';
import FormErrors from 'components/formParts/formErrors';
import { decodeFirebaseError } from 'utils/firebaseHelpers';
import { ifPasswordIsSecure } from 'utils/validation';
import useLayout from 'hooks/useLayout';
import useAuth from 'hooks/useAuth';
import Form from 'components/forms/form';
import { ReactComponent as SignUpIcon } from "assets/icons/group_add.svg"

const ClaimAccountPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { addToast } = useLayout();
    const { claimAccountWithToken, isClaimingAccount } = useAuth();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<string[]>([]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            setErrors([t('claimPasswordMismatch')]);
            return;
        }
        
        if (!ifPasswordIsSecure(password)) {
            setErrors([t('weakPassword')]);
            return;
        }
        
        if (!token) {
            setErrors([t('claimTokenInvalid')]);
            return;
        }
        
        setErrors([]);
        
        try {
            await claimAccountWithToken({ token, password });
            addToast({ text: t('claimAccountSuccess'), colorScheme: 'green' });
            navigate('/login');
        } catch (error: any) {
            let errorMessage = decodeFirebaseError(error?.code || error?.message || 'Unknown error');
            switch (errorMessage) {
                case('functions/internal'):
                    errorMessage = t('errorFailedToClaimAccount')
                    break;
            }
            setErrors([errorMessage]);
            addToast({ text: t('claimAccountError'), colorScheme: 'red' });
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
    };

    return (
        <>
            <PageTitle title={t('titleClaimAccount')} />
            <Section>
                <p>{t('claimAccountInstructions')}</p>
            </Section>
            <Section>
                <Form
                    onSubmit={handleSubmit}
                    className="group"
                    isLoading={isClaimingAccount}
                >
                    <Section design="card">
                        
                        <PasswordInput
                            label={t('claimPasswordLabel')}
                            value={password}
                            onChange={handlePasswordChange}
                            autoComplete="new-password"
                            required
                        />
                        
                        <PasswordInput
                            label={t('claimPasswordConfirmLabel')}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            autoComplete="confirm-password"
                            required
                        />
                        <ButtonGroup>
                            {isClaimingAccount ? (
                                <ProcessingButton design="primary" />
                            ) : (
                                <Button wideOnMobile 
                                    type="submit" 
                                    design="primary"
                                    disabled={!password || !confirmPassword}
                                >
                                    <SignUpIcon />
                                    <span>{t('claimButtonText')}</span>
                                </Button>
                            )}
                        </ButtonGroup>
                    </Section>
                    
                    {errors.length > 0 && <FormErrors errors={errors} />}
                </Form>
            </Section>
        </>
    );
};

export default ClaimAccountPage;
